import { Link, LinkProps } from '@/components/Link';
import { StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { getUrl } from '@/utils/getUrl';
import { mergeOptions } from '@/utils/merge';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Pagination } from './Pagination';

interface ExtendedOptions {
  $arrowLink?: LinkProps['options'];
  $buttonLink?: LinkProps['options'];
}
export interface StandalonePaginationProps extends StandaloneComponentProps<typeof Pagination, ExtendedOptions> {
  pages: number;
  page: number;
  maxSteps?: number;
  withEdgePages?: boolean;
}

export const StandalonePagination: StandaloneComponent<StandalonePaginationProps> = ({
  pages,
  page,
  maxSteps = 5,
  withEdgePages,
  ...props
}) => {
  const { asPath } = useRouter();
  const url = getUrl(asPath) as URL;
  const pathnameParts = url.pathname.split('/');
  const basePath = Number(pathnameParts[pathnameParts.length - 1])
    ? pathnameParts.slice(0, -1).join('/')
    : url.pathname;

  if (pages < 2) {
    return null;
  }

  const steps = pages > maxSteps ? maxSteps : pages;

  let paginationSteps: number[] = [];

  const closeToStartEdge = page <= steps - 1;
  const closeToEndEdge = pages - page <= Math.ceil(steps / 2);
  const displayEdgePages = pages > maxSteps ? withEdgePages : false;

  if (!displayEdgePages) {
    const startFrom = Math.min(pages - steps + 1, Math.max(1, page - Math.floor(steps / 2)));
    paginationSteps = Array.from({ length: steps }, (_, index) => startFrom + index);
  } else if (closeToStartEdge && closeToEndEdge) {
    paginationSteps = Array.from({ length: pages }, (_, index) => index + 1);
  } else if (closeToStartEdge) {
    paginationSteps = Array.from({ length: steps }, (_, index) => index + 1);
  } else if (closeToEndEdge) {
    paginationSteps = Array.from({ length: steps }, (_, index) => pages - steps + index + 1);
  } else {
    paginationSteps = Array.from({ length: steps - 2 }, (_, index) => page - Math.ceil((steps - 3) / 2) + index);
  }

  const leftArrowHref = page === 2 ? basePath : `${basePath}/${page - 1}`;
  const rightArrowHref = `${basePath}/${page + 1}`;

  return (
    <>
      <Head>
        {page < pages && <link rel="next" href={rightArrowHref} key="paginationNext" /> }
        {page > 1 && <link rel="prev" href={leftArrowHref} key="paginationPrev"/> }
      </Head>
    
      <Pagination {...props}>
        {page !== 1 && (
          <Link
            href={leftArrowHref}
            options={props.options?.$arrowLink}
            content={
              <Pagination.Icon
                name="sliderForwardArrow"
                options={mergeOptions({ className: 'rotate-180' }, props?.options?.$icon)}
              />
            }
            aria-label="Previous page"
          />
        )}

        {displayEdgePages && !closeToStartEdge && (
          <>
            <Link
              href={basePath}
              content={<Pagination.Button>1</Pagination.Button>}
              aria-label="Page 1"
              {...props.options?.$buttonLink}
            />
            <Pagination.Dots>...</Pagination.Dots>
          </>
        )}

        {paginationSteps.map((step) => (
          <Link
            key={step}
            href={step === 1 ? basePath : `${basePath}/${step}`}
            content={<Pagination.Button data-active={step === page}>{step}</Pagination.Button>}
            aria-label={`Page ${step}`}
            {...props.options?.$buttonLink}
          />
        ))}

        {displayEdgePages && !closeToEndEdge && (
          <>
            <Pagination.Dots>...</Pagination.Dots>
            <Link
              href={`${basePath}/${pages}`}
              content={<Pagination.Button>{pages}</Pagination.Button>}
              aria-label={`Page ${pages}`}
              {...props.options?.$buttonLink}
            />
          </>
        )}

        {page < pages && (
          <Link
            href={rightArrowHref}
            options={props.options?.$arrowLink}
            content={<Pagination.Icon name="sliderForwardArrow" options={props?.options?.$icon} />}
            aria-label="Next page"
          />
        )}
      </Pagination>
    </>
  );
};

export default StandalonePagination;
